import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  auth,
  googleProvider,
  signInWithPopup,
  signOut,
  database,
  ref,
  set,
  get,
} from "../../firebase-config";
import {
  setSessionTimeout,
  clearSessionTimeout,
  handleSessionExpiration,
} from "../../shared/session";
import {
  clearUserLocalStorage,
  saveUserToLocalStorage,
  getUserFromLocalStorage,
} from "../../shared/localStorage";
import {
  checkAllowedEmail,
  saveUserDataToDatabase,
  handleSignInError,
} from "../utils/authUtils";
import formatToWIB from "../../shared/dateUtils";
import CustomToast from "../../shared/CustomToast";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastTitle, setToastTitle] = useState("");
  const [toastBgColor, setToastBgColor] = useState("light");
  const [toastActionType, setToastActionType] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionTimeoutRef = useRef(null);
  const navigate = useNavigate();

  const showToastMessage = useCallback((title, message, bgColor) => {
    setToastTitle(title);
    setToastMessage(message);
    setToastBgColor(bgColor);
    setToastActionType(bgColor);
    setShowToast(true);
  }, []);

  const resetSessionTimeout = useCallback(() => {
    clearSessionTimeout(sessionTimeoutRef);
    if (user) {
      sessionTimeoutRef.current = setSessionTimeout(() =>
        handleSessionExpiration(showToastMessage, setSessionExpired)
      );
    }
  }, [user, showToastMessage]);

  const signInWithGoogle = async () => {
    setShowToast(false);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const userData = result.user;
      const email = userData.email;

      const allowedEmailEntry = await checkAllowedEmail(database, email);

      if (!allowedEmailEntry) {
        showToastMessage(
          "Email Not Allowed",
          "You do not have permission to access this website. Please contact HRD UPJ.",
          "danger"
        );
        return;
      }

      const [entryKey, entryData] = allowedEmailEntry;
      const updatedAllowedEmailData = {
        ...entryData,
        status: "Logged In",
      };
      const specificAllowedEmailRef = ref(
        database,
        `allowedEmails/${entryKey}`
      );
      await set(specificAllowedEmailRef, updatedAllowedEmailData);

      // Check if the user was previously added but then removed from allowedEmails
      const allUsersRef = ref(database, "users");
      const allUsersSnapshot = await get(allUsersRef);
      let existingUid = null;

      if (allUsersSnapshot.exists()) {
        const users = allUsersSnapshot.val();
        for (const [uid, user] of Object.entries(users)) {
          if (user.email === email) {
            existingUid = uid;
            break;
          }
        }
      }

      const userRef = ref(database, "users/" + (existingUid || userData.uid));
      const userSnapshot = await get(userRef);
      let userDetails;

      if (userSnapshot.exists()) {
        userDetails = userSnapshot.val();
        userDetails.updated_at = formatToWIB(new Date());
        await set(userRef, userDetails);
      } else {
        userDetails = {
          name: userData.displayName,
          email: userData.email,
          photo_url: userData.photoURL,
          uid: userData.uid,
          status_tes: "Pending Test",
          updated_at: formatToWIB(new Date()),
        };
        await saveUserDataToDatabase(database, userDetails);
      }

      updateUserLoginStatus(userDetails);
      navigate("/home");
    } catch (error) {
      handleSignInError(showToastMessage, error);
    }
  };

  const updateUserLoginStatus = useCallback(
    (userData) => {
      setIsLoggedIn(true);
      setUser({
        uid: userData.uid,
        name: userData.name,
        email: userData.email,
        photo_url: userData.photo_url,
        status_tes: userData.status_tes,
      });
      saveUserToLocalStorage({
        uid: userData.uid,
        name: userData.name,
        email: userData.email,
        photo_url: userData.photo_url,
        status_tes: userData.status_tes,
      });
      showToastMessage(
        "Sign in Successful",
        `Welcome ${userData.name}!`,
        "success"
      );
      setSessionExpired(false);
    },
    [showToastMessage]
  );

  const logout = useCallback(() => {
    clearUserLocalStorage();
    localStorage.removeItem("sessionExpired");
    setIsLoggedIn(false);
    setUser(null);
    signOut(auth);
    navigate("/");
    showToastMessage(
      "Sign out Successful",
      "You have been signed out of your account.",
      "success"
    );
    setSessionExpired(false);
    clearSessionTimeout(sessionTimeoutRef);
  }, [navigate, showToastMessage]);

  const extendSession = useCallback(() => {
    setShowToast(false);
    setSessionExpired(false);
    localStorage.removeItem("sessionExpired");
    resetSessionTimeout();
  }, [resetSessionTimeout]);

  const handleCancelSession = useCallback(() => {
    clearUserLocalStorage();
    localStorage.removeItem("sessionExpired");
    setIsLoggedIn(false);
    setUser(null);
    navigate("/");
    setSessionExpired(false);
    clearSessionTimeout(sessionTimeoutRef);
    showToastMessage(
      "Session Canceled",
      "Your session has been canceled.",
      "success"
    );
  }, [navigate, showToastMessage]);

  useEffect(() => {
    const { loggedIn, storedUser, sessionExpired } = getUserFromLocalStorage();

    if (sessionExpired) {
      logout();
    } else if (loggedIn && storedUser) {
      setUser(storedUser);
      setIsLoggedIn(true);
    }
    setInitialized(true);
  }, [logout]);

  useEffect(() => {
    const handleUserActivity = () => {
      if (!sessionExpired) {
        resetSessionTimeout();
      }
    };

    const activityEvents = ["mousemove", "mousedown", "keydown", "touchstart"];

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    resetSessionTimeout();
    return () => {
      clearSessionTimeout(sessionTimeoutRef);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [user, sessionExpired, resetSessionTimeout]);

  // Check session expiration on page load/refresh
  useEffect(() => {
    const { sessionExpired } = getUserFromLocalStorage();
    if (sessionExpired) {
      logout();
    }
  }, [logout]);

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(128, 128, 128, 0.5)",
    zIndex: 4,
    display: sessionExpired ? "block" : "none",
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        user,
        initialized,
        signInWithGoogle,
        logout,
        extendSession,
      }}
    >
      <div style={overlayStyle}></div>
      {children}
      <CustomToast
        showToast={showToast}
        setShowToast={setShowToast}
        title={toastTitle}
        body={toastMessage}
        bgColor={toastBgColor}
        actionType={toastActionType}
        onExtendSession={
          toastActionType === "warning" ? extendSession : undefined
        }
        onCancelSession={
          toastActionType === "warning" ? handleCancelSession : undefined
        }
      />
    </AuthContext.Provider>
  );
};

export { AuthContext };
